import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import en from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { IReconciliation } from '@/modules/workingDayReconciliation/types';
import { Avatar } from '@/shared/components/atoms';
import {
  APPROVAL_STATUS,
  APPROVAL_STATUS_LABELS,
} from '@/shared/constant/backendStatus';
import { clsxm } from '@/shared/lib';

import CheckMarkWithoutBG from '~/images/shared/CheckMarkWithoutBG.svg';
import CloseWithoutBG from '~/images/shared/CloseWithoutBG.svg';
import DashWithoutBG from '~/images/shared/DashWithoutBG.svg';

interface Props {
  jobInstanceId: number;
  approvers: IReconciliation.IApprovalsDetails[];
}
const ApprovalStatusPopover: React.FC<Props> = ({
  jobInstanceId,
  approvers,
}) => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();
  return (
    <div className='max-w-xs rounded-2xl bg-primary-99 px-16 py-24'>
      <div className='mb-16 flex w-full flex-col border-b-xs border-b-background-neutral-100 pb-2'>
        <div className='font-Cairo text-base font-bold text-black'>
          {t('approval-details')}
        </div>
        <div className='text-12 font-medium text-text-default'>
          {t('job-instance')} - {jobInstanceId}
        </div>
      </div>
      <div className='flex flex-col gap-2'>
        {approvers?.map((item, index) => (
          <div className='flex gap-16' key={item.id}>
            <div className='flex flex-col items-center gap-1'>
              <div>
                {item.approvalStatus === APPROVAL_STATUS.APPROVED ||
                item.approvalStatus === APPROVAL_STATUS.EDITED_AND_APPROVED ? (
                  <Avatar
                    icon={
                      <span
                        className={clsxm(
                          item.activeApprover
                            ? 'text-secondary-10'
                            : 'text-surface-10'
                        )}
                      >
                        <CheckMarkWithoutBG />
                      </span>
                    }
                    size='md'
                    variant='single'
                    className='w-10 bg-secondary-90 bg-opacity-5'
                  />
                ) : item.approvalStatus === APPROVAL_STATUS.PENDING ||
                  item.approvalStatus === APPROVAL_STATUS.EDITED_AND_PENDING ||
                  item.approvalStatus ===
                    APPROVAL_STATUS.REJECTED_AND_PENDING ? (
                  <Avatar
                    icon={<DashWithoutBG />}
                    size='md'
                    variant='single'
                    className={clsxm(
                      'w-10',
                      item.activeApprover
                        ? ' bg-neutralVariant-96  bg-opacity-5'
                        : 'bg-neutralVariant-90'
                    )}
                  />
                ) : (
                  <Avatar
                    icon={
                      <span
                        className={clsxm(
                          item.activeApprover
                            ? 'text-error-10'
                            : 'text-surface-10'
                        )}
                      >
                        <CloseWithoutBG />
                      </span>
                    }
                    size='md'
                    variant='single'
                    className='w-10 bg-error-90 bg-opacity-5'
                  />
                )}
              </div>
              {index < approvers.length - 1 && (
                <div className='h-[33px] w-[1px] bg-neutralVariant-90' />
              )}
            </div>
            <div className='flex flex-col'>
              <div className='text-12 font-medium text-surface-10'>
                <div className='inline-flex'>
                  {' '}
                  {t(APPROVAL_STATUS_LABELS?.[item.approvalStatus])}
                </div>
                {item.statusUpdateDate &&
                  APPROVAL_STATUS_LABELS?.[item.approvalStatus] !==
                    'pending' && (
                    <>
                      {' '}
                      -{' '}
                      {format(
                        new Date(item.statusUpdateDate),
                        'dd/MM/yyyy, hh:mm a',
                        {
                          locale:
                            locale === 'ar'
                              ? ar
                              : locale === 'es'
                              ? es
                              : locale === 'fr'
                              ? fr
                              : en,
                        }
                      )}
                    </>
                  )}
              </div>
              <div
                className={clsxm(
                  'flex items-center gap-2 text-base font-bold',
                  item.activeApprover ? ' text-text-default' : 'text-surface-10'
                )}
              >
                <span>{item.approverName}</span>
                {item.activeApprover === 0 && (
                  <span className='rounded-md bg-neutralVariant-90 py-1 px-2 text-12 font-regular'>
                    {t('inactive')}
                  </span>
                )}
              </div>
              <div className='text-14 font-regular text-surface-10'>
                {item.approverJobTitle}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ApprovalStatusPopover;
